"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

/**
 * Scroll-driven progress line that "draws" itself as user scrolls through the section.
 * Used for timeline-style content.
 */
interface ScrollProgressLineProps {
    className?: string;
    color?: string;
}

export function ScrollProgressLine({
    className,
    color = "linear-gradient(180deg, #3B82F6, #06B6D4, #A855F7)",
}: ScrollProgressLineProps) {
    const ref = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.8", "end 0.5"],
    });

    const height = useSpring(
        useTransform(scrollYProgress, [0, 1], ["0%", "100%"]),
        { stiffness: 80, damping: 25 }
    );

    const glowOpacity = useSpring(
        useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0.5]),
        { stiffness: 100, damping: 30 }
    );

    return (
        <div ref={ref} className={`relative ${className || ""}`}>
            {/* Background track */}
            <div className="absolute left-[23px] top-0 bottom-0 w-px bg-white/5 sm:left-[27px]" />

            {/* Animated progress */}
            <motion.div
                className="absolute left-[23px] top-0 w-px sm:left-[27px] z-10"
                style={{
                    height,
                    background: color,
                    boxShadow: "0 0 8px rgba(6, 182, 212, 0.4)",
                }}
            />

            {/* Glow dot at the end of progress */}
            <motion.div
                className="absolute left-[19px] w-[9px] h-[9px] rounded-full sm:left-[23px] z-20"
                style={{
                    top: height,
                    background: "#06B6D4",
                    boxShadow: "0 0 16px rgba(6, 182, 212, 0.8), 0 0 32px rgba(6, 182, 212, 0.4)",
                    opacity: glowOpacity,
                }}
            />
        </div>
    );
}
